import React from 'react';
import { 
  BarChart2, FileText, MessageCircle, Phone, Smartphone, 
  User, RefreshCw, Calendar, Home, DollarSign, 
  Sparkles, Edit, Bot, Clock, Settings, Send, Trash2, CheckCircle2, Activity 
} from 'lucide-react';

/**
 * Checks if a lead stage indicates a won/booked deal
 */
export const isWonStage = (stage) => {
  if (!stage) return false;
  const s = stage.toLowerCase();
  return s === 'won' || s.includes('confirm') || s.includes('book');
};

/**
 * Safely converts a value to a number for amounts
 */
export const safeAmount = (val) => {
  if (val === undefined || val === null || val === '') return 0;
  if (typeof val === 'number') return val;
  const str = String(val).replace(/[^0-9.-]+/g, "");
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
};

/**
 * Normalizes Pakistani phone numbers to 03XXXXXXXXX format
 */
export const normalizePakPhone = (input) => {
  if (!input) return null;
  let clean = String(input).replace(/\D/g, '');
  if (clean.startsWith('92') && clean.length === 12) clean = '0' + clean.substring(2);
  if (clean.startsWith('0092') && clean.length === 14) clean = '0' + clean.substring(4);
  if (clean.length === 10 && clean.startsWith('3')) clean = '0' + clean;
  if (clean.length === 11 && clean.startsWith('03')) return clean;
  return null;
};

/**
 * Formats a number as Pakistani Rupees
 */
export const formatCurrency = (val) => {
  return new Intl.NumberFormat('en-PK', {
    style: 'currency',
    currency: 'PKR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(safeAmount(val));
};

/**
 * Generates a WhatsApp link for a phone number
 */
export const getWhatsappLink = (phone, message = '') => {
  if (!phone) return null;
  let clean = String(phone).replace(/\D/g, '');
  if (clean.startsWith('0')) clean = '92' + clean.substring(1);
  else if (clean.length === 10 && clean.startsWith('3')) clean = '92' + clean;
  const baseUrl = `https://wa.me/${clean}`;
  return message ? `${baseUrl}?text=${encodeURIComponent(message)}` : baseUrl;
};

/**
 * Determines lead stage based on status and notes
 */
export const determineStage = (status, notes, amount) => {
  const n = (notes || '').toLowerCase();
  const s = (status || '').toLowerCase();
  const combined = s + ' ' + n;

  if (combined.includes('lost') || combined.includes('cancel')) return 'Lost';
  if (s.includes('won') || s.includes('book') || s.includes('confirm')) return 'Won';
  if (n.includes('advance') || n.includes('deposit') || n.includes('closed')) return 'Won';
  if (combined.includes('visit') || combined.includes('meeting')) return 'Negotiation';
  if (combined.includes('budget') || combined.includes('quote')) return 'Proposal';
  return 'New';
};

/**
 * Determines manager name from partial match
 */
export const determineManager = (manager) => {
  if (!manager) return 'Unassigned';
  const m = String(manager).toLowerCase();
  if (m.includes('zia')) return 'Zia un Nabi';
  if (m.includes('rafaqat')) return 'Rafaqat Ali';
  if (m.includes('hassan')) return 'Hassan Rizvi';
  if (m.includes('khizar')) return 'Khizar';
  return manager;
};

/**
 * Converts a date or timestamp to a YYYY-MM-DD string in the browser's local timezone (e.g. PKT).
 * Prevents UTC timezone shifts where late-night/early-morning timestamps appear as the previous day.
 */
export const toLocalDateString = (dateInput) => {
  if (!dateInput) return '';
  if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    return dateInput;
  }
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Formats a date using the en-GB locale (Day/Month/Year format)
 * @param {string|Date} dateInput 
 * @param {Object} options 
 */
export const formatDate = (dateInput, options = {}) => {
  if (!dateInput) return '-';
  let d;
  if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    const [year, month, day] = dateInput.split('-').map(Number);
    d = new Date(year, month - 1, day);
  } else {
    d = new Date(dateInput);
  }
  if (isNaN(d.getTime())) return '-';
  
  // Default to day/month/year format unless overridden by specific options
  const formatOptions = Object.keys(options).length > 0 ? options : { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  };
  
  return d.toLocaleDateString('en-GB', formatOptions);
};

/**
 * Activity Log Types
 */
export const ACTIVITY_TYPES = {
  STAGE_CHANGE: 'stage_change',
  NOTE_ADDED: 'note_added',
  WHATSAPP_OPENED: 'whatsapp_opened',
  CALL_MADE: 'call_made',
  CALL_LOGGED: 'call_logged',
  LEAD_ASSIGNED: 'lead_assigned',
  LEAD_REASSIGNED: 'lead_reassigned',
  FOLLOW_UP_SET: 'follow_up_set',
  SITE_VISIT_SCHEDULED: 'site_visit_scheduled',
  PAYMENT_RECEIVED: 'payment_received',
  LEAD_CREATED: 'lead_created',
  DETAILS_UPDATED: 'details_updated',
  AUTO_GREETING_SENT: 'auto_greeting_sent',
  QUOTE_REMINDER_SENT: 'quote_reminder_sent',
  SYSTEM: 'system',
  MESSAGE_SENT: 'message_sent',
  PAYMENT_ADDED: 'payment_added',
  PAYMENT_DELETED: 'payment_deleted',
  PAYMENT_COMPLETED: 'payment_completed',
  BMS_SYNC: 'bms_sync'
};

/**
 * Creates an activity log entry
 */
export const createActivityEntry = (type, data, userName) => {
  return {
    type,
    timestamp: new Date().toISOString(),
    user: userName,
    ...data
  };
};


/**
 * Gets icon and color for activity type
 */
export const getActivityMeta = (type) => {
  const t = (type || '').toLowerCase();
  const meta = {
    [ACTIVITY_TYPES.STAGE_CHANGE]: { icon: <BarChart2 size={16} />, color: 'blue', label: 'Stage Changed' },
    [ACTIVITY_TYPES.NOTE_ADDED]: { icon: <FileText size={16} />, color: 'gray', label: 'Note Added' },
    [ACTIVITY_TYPES.WHATSAPP_OPENED]: { icon: <MessageCircle size={16} />, color: 'green', label: 'WhatsApp' },
    [ACTIVITY_TYPES.CALL_MADE]: { icon: <Phone size={16} />, color: 'purple', label: 'Call Made' },
    [ACTIVITY_TYPES.CALL_LOGGED]: { icon: <Smartphone size={16} />, color: 'cyan', label: 'Call Logged' },
    [ACTIVITY_TYPES.LEAD_ASSIGNED]: { icon: <User size={16} />, color: 'indigo', label: 'Assigned' },
    [ACTIVITY_TYPES.LEAD_REASSIGNED]: { icon: <RefreshCw size={16} />, color: 'orange', label: 'Reassigned' },
    [ACTIVITY_TYPES.FOLLOW_UP_SET]: { icon: <Calendar size={16} />, color: 'amber', label: 'Follow-up Set' },
    [ACTIVITY_TYPES.SITE_VISIT_SCHEDULED]: { icon: <Home size={16} />, color: 'teal', label: 'Site Visit' },
    [ACTIVITY_TYPES.PAYMENT_RECEIVED]: { icon: <DollarSign size={16} />, color: 'emerald', label: 'Payment' },
    [ACTIVITY_TYPES.LEAD_CREATED]: { icon: <Sparkles size={16} />, color: 'blue', label: 'Lead Created' },
    [ACTIVITY_TYPES.DETAILS_UPDATED]: { icon: <Edit size={16} />, color: 'slate', label: 'Updated' },
    [ACTIVITY_TYPES.AUTO_GREETING_SENT]: { icon: <Bot size={16} />, color: 'green', label: 'Auto Greeting' },
    [ACTIVITY_TYPES.QUOTE_REMINDER_SENT]: { icon: <Clock size={16} />, color: 'amber', label: 'Quote Reminder' },
    [ACTIVITY_TYPES.SYSTEM]: { icon: <Settings size={16} />, color: 'slate', label: 'System' },
    [ACTIVITY_TYPES.MESSAGE_SENT]: { icon: <Send size={16} />, color: 'blue', label: 'Message Sent' },
    [ACTIVITY_TYPES.PAYMENT_ADDED]: { icon: <DollarSign size={16} />, color: 'green', label: 'Payment Added' },
    [ACTIVITY_TYPES.PAYMENT_DELETED]: { icon: <Trash2 size={16} />, color: 'red', label: 'Payment Deleted' },
    [ACTIVITY_TYPES.PAYMENT_COMPLETED]: { icon: <CheckCircle2 size={16} />, color: 'green', label: 'Payment Completed' },
    [ACTIVITY_TYPES.BMS_SYNC]: { icon: <RefreshCw size={16} />, color: 'emerald', label: 'BMS Sync' }
  };
  return meta[t] || { icon: <Activity size={16} />, color: 'gray', label: 'Activity' };
};

/**
 * Formats activity for display
 */
export const formatActivityMessage = (activity) => {
  const type = (activity.type || '').toLowerCase();
  switch (type) {
    case ACTIVITY_TYPES.STAGE_CHANGE: {
      let msg = `Stage changed: ${activity.from} → ${activity.to}`;
      if (activity.lossReason) {
        msg += ` — ${activity.lossReason}`;
      }
      if (activity.editedFields) {
        msg += ` (${activity.editedFields} updated)`;
      }
      if (activity.bookingDetails) {
        const bd = activity.bookingDetails;
        const parts = [];
        if (bd.venue) parts.push(`Venue: ${bd.venue}`);
        if (bd.eventDate) parts.push(`Date: ${formatDate(bd.eventDate)}`);
        if (bd.finalAmount) parts.push(`Amount: ${formatCurrency(Number(bd.finalAmount))}`);
        if (bd.advanceAmount) parts.push(`Advance: ${formatCurrency(Number(bd.advanceAmount))}`);
        if (parts.length > 0) msg += ` | ${parts.join(', ')}`;
      }
      if (activity.note) {
        msg += ` (Note: ${activity.note})`;
      }
      return msg;
    }
    case ACTIVITY_TYPES.PAYMENT_ADDED:
      return `Payment of ${formatCurrency(activity.amount)} received (${activity.paymentType}). Remaining: ${formatCurrency(activity.remaining)}.`;
    case ACTIVITY_TYPES.PAYMENT_DELETED:
      return `Payment of ${formatCurrency(activity.amount)} (${activity.paymentType}) deleted.`;
    case ACTIVITY_TYPES.PAYMENT_COMPLETED:
        return `All payments completed. Total paid: ${formatCurrency(activity.totalPaid)}.`;
    case ACTIVITY_TYPES.NOTE_ADDED:
      return activity.note || 'Note added';
    case ACTIVITY_TYPES.WHATSAPP_OPENED:
      return 'WhatsApp conversation opened';
    case ACTIVITY_TYPES.CALL_MADE:
      return 'Phone call made';
    case ACTIVITY_TYPES.CALL_LOGGED: {
      const outcomeMap = {
        'connected_interested': 'Connected - Interested',
        'connected_not_interested': 'Connected - Not Interested',
        'connected_callback': 'Connected - Callback Requested',
        'no_answer': 'No Answer',
        'busy': 'Busy / Call Later',
        'wrong_number': 'Wrong Number'
      };
      const friendlyOutcome = outcomeMap[activity.outcome] || activity.outcome;
      let callMsg = friendlyOutcome ? `Call logged: ${friendlyOutcome}` : 'Call logged';
      if (activity.notes) {
        callMsg += `\nNotes: ${activity.notes}`;
      }
      return callMsg;
    }
    case ACTIVITY_TYPES.LEAD_ASSIGNED:
      return `Lead assigned to ${activity.assignee}`;
    case ACTIVITY_TYPES.LEAD_REASSIGNED:
      return `Reassigned from ${activity.from} to ${activity.to}`;
    case ACTIVITY_TYPES.FOLLOW_UP_SET:
      return `Follow-up scheduled for ${activity.date}`;
    case ACTIVITY_TYPES.SITE_VISIT_SCHEDULED:
      return `Site visit scheduled for ${activity.date}`;
    case ACTIVITY_TYPES.PAYMENT_RECEIVED:
      return `Payment received: ${formatCurrency(activity.amount)}`;
    case ACTIVITY_TYPES.LEAD_CREATED:
      return 'Lead created';
    case ACTIVITY_TYPES.DETAILS_UPDATED:
      return `${activity.field} updated`;
    case ACTIVITY_TYPES.AUTO_GREETING_SENT:
      return 'Auto greeting sent via WhatsApp';
    case ACTIVITY_TYPES.QUOTE_REMINDER_SENT:
      return 'Quote follow-up reminder sent';
    case ACTIVITY_TYPES.SYSTEM:
      return activity.note || activity.content || 'System notification';
    case ACTIVITY_TYPES.MESSAGE_SENT:
      return activity.message || 'WhatsApp message sent';
    case ACTIVITY_TYPES.BMS_SYNC:
    case 'bms_sync':
    case 'BMS_SYNC':
      return activity.content || activity.note || '🔄 BMS Sync — Booking updated';
    default:
      return activity.content || activity.note || activity.message || 'Activity logged';
  }
};
